import React from "react";
import World from './zone-names.json';
import {closest, distance} from "fastest-levenshtein";
import ReactDOM from "react-dom";
import {Checkbox} from "semantic-ui-react";
import {initOcr, detectText, getBackend} from "./ocr";

const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const debugLog = (...args) => { if (isLocalhost) console.log(...args); };

const byDisplayname = World.reduce((lookup, cluster) => {
    const displayname = cluster.displayname.trim();
    const entries = lookup.get(displayname) || [];
    entries.push(cluster);
    lookup.set(displayname, entries);
    return lookup;
}, new Map());
const displaynames = Array.from(byDisplayname.keys());

function displaynameFor(location) {
    return location ? String(location).split("#")[0] : null;
}

function resolveDisplayname(displayname, previousZone) {
    const entries = byDisplayname.get(displayname) || [];
    if (entries.length <= 1) return displayname;

    const previousDisplayname = displaynameFor(previousZone);
    const matches = entries.filter(entry =>
        previousDisplayname && (entry.parents || []).includes(previousDisplayname));
    if (matches.length === 1) {
        return `${displayname}#${matches[0].id}`;
    }

    debugLog(`[zone-ocr] ambiguous zone "${displayname}" with previousZone="${previousDisplayname || "none"}" (${matches.length} parent matches); using bare displayname`);
    return displayname;
}

function isUnresolvedDisplayname(location) {
    if (!location || String(location).includes("#")) return false;
    return (byDisplayname.get(displaynameFor(location)) || []).length > 1;
}

const displayMediaOptions = {
    video: {
        cursor: "always"
    },
    audio: false
}

function matchZone(raw, previousZone = null) {
    if (!raw || raw.length < 4 || !/[a-z]/i.test(raw)) return null;
    const lower = raw.toLowerCase();
    let best = null;
    for (const name of displaynames) {
        if (lower.includes(name.toLowerCase()) && (!best || name.length > best.length)) {
            best = name;
        }
    }
    if (best) return resolveDisplayname(best, previousZone);
    const candidate = closest(raw, displaynames);
    return distance(raw, candidate) <= 2
        ? resolveDisplayname(candidate, previousZone)
        : null;
}

// Find the closest match
function findMatch(color) {
    const match = [
        {r: 54, g: 155, b: 204},
        {r: 177, g: 129, b: 19}
    ];
    let bestDistance = -1;
    let bestMatch;
    for (let i = 0; i < match.length; i++) {
        let distance = (color.r - match[i].r) * (color.r - match[i].r) +
            (color.g - match[i].g) * (color.g - match[i].g) +
            (color.b - match[i].b) * (color.b - match[i].b);
        if (bestDistance === -1 || bestDistance > distance) {
            bestDistance = distance;
            bestMatch = i;
        }
    }
    switch (bestMatch) {
        case 0:
            return 7;
        case 1:
            return 20;
        default:
            return 7;
    }
}

function getColor(data) {
    const length = data.data.length;
    let i = 0;
    let count = 0;
    let rgb = {r: 0, g: 0, b: 0};
    while ((i += 4) < length) {
        ++count;
        rgb.r += data.data[i];
        rgb.g += data.data[i + 1];
        rgb.b += data.data[i + 2];
    }

    // ~~ used to floor values
    rgb.r = ~~(rgb.r / count);
    rgb.g = ~~(rgb.g / count);
    rgb.b = ~~(rgb.b / count);

    return rgb;
}

// const copyColors = [
//     {r: 0xFF, g: 0xFF, b: 0xFF}, // text
//     {r: 0xFF, g: 0x54, b: 0x3C}, // red
//     {r: 0xFF, g: 0x4E, b: 0x39}, // red
//     {r: 0xFB, g: 0x4E, b: 0x3a}, // red
//     {r: 0xFF, g: 0x56, b: 0x3F}, // red
//     {r: 0xd8, g: 0x5e, b: 0x51}, // red
//     {r: 0xe1, g: 0xcb, b: 0xbf}, // closes in
// ]
//
// function checkColors(data, i) {
//     //     text: ffffff size: 4b2b08 green: bbf630
//     //     road of avalon: d2bcaf red: ff543c
//     let r = data[i + 0];
//     let g = data[i + 1];
//     let b = data[i + 2];
//     return copyColors.find(rgb => {
//         return Math.abs(r - rgb.r) + Math.abs(g - rgb.g) + Math.abs(b - rgb.b) < 30;
//     });
// }

function Capture(props) {
    const videoElem = React.useRef(null);
    const mapElem = React.useRef(null);
    const zoneElem = React.useRef(null);
    const sizeElem = React.useRef(null);
    const portalElem = React.useRef(null);
    const portal2Elem = React.useRef(null);
    const expiresElem = React.useRef(null);
    const scaledElem = React.useRef(null);
    const [started, setStarted] = React.useState(false);
    const [road, setRoad] = React.useState({zone: null, portal: null, expires: null, size: null});
    const roadRef = React.useRef(road);
    const previousZoneRef = React.useRef(null);
    React.useEffect(() => { roadRef.current = road; }, [road]);
    const {
        updateRoad,
        updateLocation,
        checkMapped,
        isMapped,
        mappedVersion = 0,
        stableRoadCount = 0,
        autoAdding = false,
    } = props;
    const [debug, setDebug] = React.useState(false);
    const [ocrLoading, setOcrLoading] = React.useState(false);
    const [ocrBackend, setOcrBackend] = React.useState(null);

    async function startCapture() {
        try {
            setOcrLoading(true);
            await initOcr();
            setOcrBackend(getBackend());
            setOcrLoading(false);
            videoElem.current.srcObject = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
            setStarted(true);
        } catch (err) {
            setOcrLoading(false);
            console.error("Error: " + err);
        }
    }

    /*
    {boundingBox: DOMRectReadOnly, cornerPoints: Array(0), rawValue: "Road of Avalon to"}
     */

    React.useEffect(() => {
        async function loop() {
            if (videoElem.current && scaledElem.current) {
                const scaled = scaledElem.current.getContext('2d', { willReadFrequently: true });
                const vw = videoElem.current.videoWidth;
                const vh = videoElem.current.videoHeight;
                const tw = vw / 3840.0;
                const hratio = 2160.0 / vh * tw;
                const offset = 3840.0 * tw - 3840.0 * tw / hratio;
                debugLog(`${vw} ${vh} ${hratio} ${offset}`);
                scaled.drawImage(videoElem.current, offset, 0, vw - offset, vh, 0, 0, 3840, 2160);
            }
            if (scaledElem.current && mapElem.current && videoElem.current && videoElem.current.videoWidth && zoneElem.current && sizeElem.current && portalElem.current && portal2Elem.current && expiresElem.current) {
                let timer = null;
                let portal = null;
                let color = null;
                const map = mapElem.current.getContext('2d', { willReadFrequently: true });
                map.drawImage(scaledElem.current, 2592, 1400, 1248, 670, 0, 0, 1248, 670);
                const mapImage = map.getImageData(0, 0, 1248, 670);
                let mapBoxes = await detectText(mapImage);
                debugLog(`[map-ocr] boxes=${mapBoxes.length} rawTexts=[${mapBoxes.map(b => b.rawValue).join(",")}]`);
                const box = mapBoxes.find(box => box.rawValue.includes("Road of Avalon"));
                if (box) {
                    const bb = box.boundingBox;

                    const portalctx = portalElem.current.getContext('2d', { willReadFrequently: true });
                    portalctx.drawImage(mapElem.current, bb.left - 137, bb.top - 23, 720, 225, 0, 0, 725, 225);

                    // Let's try and figure out the size
                    const size = sizeElem.current.getContext('2d', { willReadFrequently: true });
                    size.drawImage(mapElem.current, bb.left - 70, bb.top + 20, 10, 10, 0, 0, 10, 10);
                    color = getColor(size.getImageData(0, 0, 10, 10));
                    // b {r: 54, g: 155, b: 204} - size 7
                    // y {r: 177, g: 129, b: 19} - size 20

                    const others = mapBoxes.filter(b => b !== box);
                    const anchorTop = bb.top;
                    const anchorBottom = bb.top + bb.height;
                    const anchorLeft = bb.left;
                    const rightCandidates = others
                        .filter(b => b.boundingBox.left > anchorLeft)
                        .sort((a, b) => Math.abs(a.boundingBox.top - anchorTop) - Math.abs(b.boundingBox.top - anchorTop));
                    const belowCandidates = others
                        .filter(b => b.boundingBox.top >= anchorBottom)
                        .sort((a, b) => a.boundingBox.top - b.boundingBox.top);
                    const candidates = [...rightCandidates, ...belowCandidates];
                    let destRaw = null;
                    let matched = null;
                    for (const cand of candidates) {
                        const m = matchZone(cand.rawValue, previousZoneRef.current);
                        if (m) {
                            destRaw = cand.rawValue;
                            matched = m;
                            break;
                        }
                    }
                    if (destRaw !== null) {
                        debugLog(`[map-ocr] destRaw="${destRaw}"`);
                        portal = matched;
                        debugLog("Road: " + portal);
                    } else {
                        debugLog(`[map-ocr] destRaw=null (no candidate matched a zone)`);
                        portal = null;
                    }

                    const timerBox = mapBoxes.find(b => /closes in/i.test(b.rawValue));
                    if (timerBox) {
                        const expires = timerBox.rawValue;
                        debugLog(`[map-ocr] timerRaw="${expires}"`);
                        const normalized = expires.replace(/([0-9])\s+([0-9])/g, "$1$2");
                        debugLog(`[map-ocr] timerNormalized="${normalized}"`);
                        let h = 0, m = 0, s = 0;
                        let matched = false;

                        const unitRe = /([0-9]{1,2})\s*([hms])/gi;
                        let lastUnit = null;
                        let lastEndIndex = 0;
                        let mm;
                        while ((mm = unitRe.exec(normalized)) !== null) {
                            matched = true;
                            const n = parseInt(mm[1], 10);
                            const u = mm[2].toLowerCase();
                            if (u === 'h') h = n;
                            else if (u === 'm') m = n;
                            else if (u === 's') s = n;
                            lastUnit = u;
                            lastEndIndex = unitRe.lastIndex;
                        }

                        if (matched && lastUnit && lastUnit !== 's') {
                            const after = normalized.substring(lastEndIndex);
                            const trailingRe = /([0-9]{1,2})/;
                            const tm = trailingRe.exec(after);
                            if (tm) {
                                const tn = parseInt(tm[1], 10);
                                if (lastUnit === 'h') m = tn;
                                else if (lastUnit === 'm') s = tn;
                                debugLog(`[map-ocr] timer trailing number ${tn} → ${lastUnit === 'h' ? 'minutes' : 'seconds'}`);
                            }
                        }

                        if (!matched) {
                            const bareRe = /closes\s*in\s*([0-9]{1,2})/i;
                            const bm = bareRe.exec(normalized);
                            if (bm) {
                                matched = true;
                                h = parseInt(bm[1], 10);
                                debugLog(`[map-ocr] timer unit missing, assumed hours=${h}`);
                            }
                        }

                        if (matched) {
                            timer = { hours: h, minutes: m, seconds: s };
                        }
                        debugLog(`[map-ocr] timerParsed=`, timer);
                    }
                }

                // 695140
                if (zoneElem.current) {
                    const zone = zoneElem.current.getContext('2d', { willReadFrequently: true });
                    zone.drawImage(scaledElem.current, 2100, 2000, 1700, 120, 0, 0, 1700, 120);
                    const zoneImage = zone.getImageData(0, 0, 1700, 120);
                    const zoneBoxes = await detectText(zoneImage);

                    let currentZone = null;
                    debugLog(`[zone-ocr] boxes=${zoneBoxes.length}`);
                    zoneBoxes.forEach(box => {
                        const raw = box.rawValue;
                        const candidate = matchZone(raw, previousZoneRef.current);
                        debugLog(`[zone-ocr] raw="${raw}" → match="${candidate}"`);
                        if (candidate) {
                            currentZone = candidate;
                            if ((byDisplayname.get(displaynameFor(candidate)) || []).length === 1) {
                                previousZoneRef.current = displaynameFor(candidate);
                            }
                            if (!candidate.startsWith("Conquerors")) {
                                debugLog("Setting current zone: " + candidate);
                                updateLocation(candidate);
                            }
                        }
                    });
                    const prevRaw = roadRef.current;
                    // If zone changed this tick, discard sticky portal/timer/size from previous zone
                    const zoneChanged = currentZone && prevRaw.zone && currentZone !== prevRaw.zone;
                    const prev = zoneChanged
                        ? { zone: currentZone, portal: null, expires: null, size: null }
                        : prevRaw;
                    if (zoneChanged) {
                        debugLog(`[zone-ocr] zone changed ${prevRaw.zone} → ${currentZone}, clearing road`);
                        updateRoad(null);
                    }
                    const detectedSize = color ? findMatch(color) : null;
                    const newRoad = {
                        zone: currentZone || prev.zone,
                        portal: portal || prev.portal,
                        expires: timer || prev.expires,
                        size: detectedSize || prev.size || 7,
                    };
                    const unresolvedRoad = isUnresolvedDisplayname(newRoad.zone)
                        || isUnresolvedDisplayname(newRoad.portal);
                    if (!unresolvedRoad && newRoad.zone && newRoad.portal && checkMapped) {
                        checkMapped(newRoad.zone, newRoad.portal);
                    }
                    if (!unresolvedRoad && (portal || timer) && newRoad.zone && newRoad.portal && newRoad.expires && newRoad.size) {
                        ReactDOM.unstable_batchedUpdates(() => {
                            setRoad(newRoad);
                            updateRoad(newRoad);
                            debugLog(newRoad);
                        });
                    } else if (unresolvedRoad || (!portal && !timer)) {
                        updateRoad(null);
                    }
                }
            }
        }

        if (started) {
            const interval = setInterval(loop, 1000);
            return () => clearInterval(interval);
        }
    }, [started, scaledElem, mapElem, videoElem, zoneElem, portalElem, sizeElem, portal2Elem, expiresElem, updateRoad, updateLocation, checkMapped, mappedVersion]);

    function stopCapture(evt) {
        let tracks = videoElem.current.srcObject.getTracks();

        tracks.forEach(track => track.stop());
        videoElem.current.srcObject = null;
        setStarted(false);
    }

    return <div>
        <br/>
        <br/>
        <p>
            {ocrLoading ?
                <span style={{"display": "block"}}>Loading OCR model…</span> : ""}
            {ocrBackend && !ocrLoading ?
                <span style={{"display": "block"}}>OCR backend: {ocrBackend}</span> : ""}
            {started ? "" :
                <button disabled={ocrLoading} onClick={startCapture} id="start">Start Road Screen Capture</button>}
            {started ? <button onClick={stopCapture} id="stop">Stop Road Screen Capture</button> : ""}
        </p>
        {road.zone ?
            <h3>Road
                Detected: {displaynameFor(road.zone)} {displaynameFor(road.portal)}{isMapped && isMapped(road.zone, road.portal) ? <span style={{color: "green"}}> ✅ mapped</span> : ""} {road.size} {road.expires ? String(road.expires.hours).padStart(2, "0") + "h " + String(road.expires.minutes).padStart(2, "0") + "m " + String(road.expires.seconds).padStart(2, "0") + "s" : ""}{autoAdding ? " (auto-adding...)" : stableRoadCount > 0 ? ` (${stableRoadCount}/2)` : ""}</h3>
            : ""}

        <canvas width={725} height={225} ref={portalElem} id="portal"/>
        <br/>
        <Checkbox id='updateDebugCheckbox'
                  onChange={(e, v) => {
                      setDebug(v.checked);
                  }} checked={debug}/>
        <span>&nbsp;Debug&nbsp;</span>

        <div style={{"display": debug ? "block" : "none"}}>
            <video ref={videoElem} id="video" autoPlay/>
            <br/>
            <canvas width={1248} height={670} ref={mapElem} id="cropped"/>
            <br/>
            <canvas width={1700} height={120} ref={zoneElem} id="currentzone"/>
            <br/>
            <canvas width={10} height={10} ref={sizeElem}/>
            <br/>
            <canvas width={725} height={225} ref={portal2Elem} id="portal2"/>
            <br/>
            <canvas width={725} height={225} ref={expiresElem} id="expires"/>
            <br/>
            <canvas width={3840} height={2160} ref={scaledElem} id="scaled"/>
            <br/>
        </div>
    </div>;
}

export {matchZone};
export default Capture;

